// ============================================================================
// roof-belegung.ts — Polygon-Belegung (echte Dachform) für die UI
// ----------------------------------------------------------------------------
// Brücke zwischen der gezeichneten Dachfläche (LatLng-Polygon + Störflächen)
// und der deterministischen roof-core-Engine (computeModuleLayout). Liefert ein
// gemeinsames lokales Meter-Frame (Ursprung = erster Polygon-Eckpunkt), in dem
// Dachumriss, platzierte Module und Hindernisse maßstäblich dargestellt werden
// können (RoofPolygonLayoutSvg).
//
// Rein/deterministisch: keine DB, kein State, kein Zufall. Gleiche Eingabe →
// gleiche Ausgabe. Berechnung kommt 1:1 aus roof-core (kein Doppel-Modell).
// ============================================================================
import { toLocalMeters } from "@energyos/geometry-core";
import { computeModuleLayout, defaultSetbacks } from "@energyos/roof-core";
import type { RoofPlaneInput, ObstacleInput, ExclusionInput } from "@energyos/roof-core";
import type { GeoPoint, ModuleSpec, SetbackConfig, ModuleOrientation } from "@energyos/shared-types";

/** Geo-Punkt wie im Zeichen-Layer (RoofDrawMap/RoofEditor). */
export interface LatLng {
  lat: number;
  lng: number;
}

/** Punkt im lokalen Meter-Frame (x = Ost, y = Nord, Ursprung = erster Eckpunkt). */
export interface LocalPoint {
  x: number;
  y: number;
}

/** Ein platziertes Modul als lokales Meter-Polygon (4 Ecken). */
export interface ModuleRectLocal {
  corners: LocalPoint[];
  /** Stabile Identität (Rasterposition "row-col") für die manuelle Bearbeitung. */
  id: string;
}

/** Eingabe der Polygon-Belegung. */
export interface BelegungInput {
  /** Gezeichnetes Dachpolygon (≥ 3 Punkte). */
  polygon: LatLng[];
  /** Störflächen/Hindernisse (Kamin, Gaube …) als Polygone. */
  obstacles?: LatLng[][];
  /** Sperrflächen/No-Go-Zonen (semanticJson.keepouts) als Polygone — Module bleiben draußen. */
  keepouts?: LatLng[][];
  /** Traufkante (gezeichnet): richtet die Modulreihen PARALLEL zu dieser Dachseite aus.
   *  Ohne Angabe = längste Polygonkante (roof-core-Default). */
  eave?: { a: LatLng; b: LatLng };
  /** Modul-Kenndaten (aus dem Belegungs-Formular). */
  moduleSpec: ModuleSpec;
  /** Dachneigung (Grad) — Annahme bis bestätigt. */
  tiltDeg?: number;
  /** Azimut (Grad, 0 = Nord). */
  azimuthDeg?: number;
  /** Randabstände; ohne Angabe = roof-core defaultSetbacks(). */
  setbacks?: SetbackConfig;
  /** Modulspalt entlang der Traufe (Meter). */
  moduleGapM?: number;
  /** zusätzlicher Reihenabstand (Meter). */
  rowGapM?: number;
}

/** Ergebnis der Polygon-Belegung im gemeinsamen lokalen Meter-Frame. */
export interface BelegungResult {
  /** Dachumriss als lokales Meter-Polygon. */
  polygonLocal: LocalPoint[];
  /** platzierte Module als lokale Meter-Polygone. */
  moduleRectsLocal: ModuleRectLocal[];
  /** platzierte Module als LatLng-Polygone (für ein Karten-Overlay im RoofEditor,
   *  gezeichnet mit map.project() — deckungsgleich mit dem Dachpolygon). */
  moduleRectsLL: { id: string; corners: LatLng[] }[];
  /** Störflächen als lokale Meter-Polygone. */
  obstaclesLocal: LocalPoint[][];
  /** Sperrflächen als lokale Meter-Polygone (für die Darstellung). */
  keepoutsLocal: LocalPoint[][];
  /** Anzahl platzierter Module. */
  count: number;
  /** installierte Leistung in kWp. */
  kwp: number;
  /** gewählte Ausrichtung (die mit mehr Modulen). */
  orientation: ModuleOrientation;
}

function toGeo(p: LatLng): GeoPoint {
  return { latitude: p.lat, longitude: p.lng };
}

function projectGeo(points: GeoPoint[], origin: GeoPoint): LocalPoint[] {
  return points.map((p) => {
    const v = toLocalMeters(p, origin);
    return { x: v.x, y: v.y };
  });
}

/**
 * Belegt das gezeichnete Dachpolygon deterministisch mit Modulen (beide
 * Orientierungen, behält die ergiebigere = „auto") und projiziert Dach, Module
 * und Störflächen in ein gemeinsames lokales Meter-Frame (Ursprung = erster
 * Polygon-Eckpunkt). Gibt bei < 3 Punkten / fehlerhafter Engine ein leeres,
 * aber valides Ergebnis zurück (UI fällt dann auf das Rechteck zurück).
 */
export function computeBelegung(input: BelegungInput): BelegungResult | null {
  const { polygon } = input;
  if (!polygon || polygon.length < 3) return null;

  const origin = toGeo(polygon[0]!);
  const vertices = polygon.map(toGeo);
  const obstaclePolys = (input.obstacles ?? []).filter((o) => o.length >= 3);
  const keepoutPolys = (input.keepouts ?? []).filter((k) => k.length >= 3);

  const polygonLocal = projectGeo(vertices, origin);
  const obstaclesLocal = obstaclePolys.map((o) => projectGeo(o.map(toGeo), origin));
  const keepoutsLocal = keepoutPolys.map((k) => projectGeo(k.map(toGeo), origin));

  const setbacks = input.setbacks ?? defaultSetbacks();
  const plane: RoofPlaneInput = {
    id: "ui-belegung",
    vertices,
    tiltDeg: input.tiltDeg ?? 30,
    azimuthDeg: input.azimuthDeg ?? 180,
    // Maßstab/Neigung gelten in der UI als bestätigt (gezeichnete Geometrie):
    scaleConfirmed: true,
    tiltConfirmed: true,
    // Traufe → Module liegen parallel zu dieser Dachseite (statt längster Kante).
    ...(input.eave ? { eave: { a: toGeo(input.eave.a), b: toGeo(input.eave.b) } } : {}),
  };
  const obstacleInputs: ObstacleInput[] = obstaclePolys.map((o, i) => ({
    id: `obs-${i}`,
    type: "chimney",
    vertices: o.map(toGeo),
  }));
  // Sperrflächen → ExclusionInput (die Zonen-Geometrie selbst blockiert die Belegung).
  const exclusionInputs: ExclusionInput[] = keepoutPolys.map((k, i) => ({
    id: `keepout-${i}`,
    kind: "keepout",
    vertices: k.map(toGeo),
  }));

  // Beide Orientierungen rechnen, die mit mehr Modulen behalten („auto").
  let best: {
    count: number;
    kwp: number;
    orientation: ModuleOrientation;
    moduleRectsLocal: ModuleRectLocal[];
    moduleRectsLL: { id: string; corners: LatLng[] }[];
  } | null = null;
  for (const orientation of ["portrait", "landscape"] as const) {
    const res = computeModuleLayout(plane, obstacleInputs, exclusionInputs, {
      moduleSpec: input.moduleSpec,
      orientation,
      moduleGapM: input.moduleGapM ?? 0.02,
      rowGapM: input.rowGapM ?? 0,
      setbacks,
    });
    const layout = res.data;
    const count = layout?.count ?? 0;
    if (best == null || count > best.count) {
      const mods = layout?.modules ?? [];
      best = {
        count,
        kwp: layout?.kwp ?? 0,
        orientation,
        moduleRectsLocal: mods.map((m, i) => ({
          corners: projectGeo(m.corners, origin),
          // Stabile ID aus Rasterposition; Fallback Index, falls row/col fehlen.
          id: m.row != null && m.col != null ? `${m.row}-${m.col}` : `i${i}`,
        })),
        // Rohe LatLng-Ecken fürs Karten-Overlay (map.project), deckungsgleich mit dem Dach.
        moduleRectsLL: mods.map((m, i) => ({
          id: m.row != null && m.col != null ? `${m.row}-${m.col}` : `i${i}`,
          corners: m.corners.map((c) => ({ lat: c.latitude, lng: c.longitude })),
        })),
      };
    }
  }

  if (!best) return null;

  return {
    polygonLocal,
    moduleRectsLocal: best.moduleRectsLocal,
    moduleRectsLL: best.moduleRectsLL,
    obstaclesLocal,
    keepoutsLocal,
    count: best.count,
    kwp: best.kwp,
    orientation: best.orientation,
  };
}
